// Tooltip.js - Reusable tooltip component with security context
// Features: 300ms delay, intelligent positioning, attack/defense icons

import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Tooltip.css';

/**
 * Tooltip Categories
 * - attack: How an attacker would exploit this (crosshairs icon)
 * - defense: How to fix/mitigate (shield icon)
 * - context: Background information (info icon)
 * - risk: Why this severity was assigned (warning icon)
 */

const CATEGORY_ICONS = {
    attack: '🎯',
    defense: '🛡️',
    context: 'ℹ️',
    risk: '⚠️'
};

const CATEGORY_LABELS = {
    attack: 'ATTACK',
    defense: 'DEFEND',
    context: 'INFO',
    risk: 'RISK'
};

/**
 * Tooltip Component
 *
 * @param {React.ReactNode} children - Element that triggers the tooltip
 * @param {string} content - Main tooltip text
 * @param {string} [category] - 'attack' | 'defense' | 'context' | 'risk'
 * @param {string} [attackText] - Attack relevance explanation
 * @param {string} [defenseText] - Defense action explanation
 * @param {string} [position] - 'top' | 'bottom' | 'left' | 'right' | 'auto' (default)
 * @param {number} [delay] - Delay before showing (default 300ms)
 * @param {boolean} [disabled] - Disable tooltip
 */
function Tooltip({
    children,
    content,
    category,
    attackText,
    defenseText,
    position = 'auto',
    delay = 300,
    disabled = false
}) {
    const [isVisible, setIsVisible] = useState(false);
    const [calculatedPosition, setCalculatedPosition] = useState('top');
    const [coords, setCoords] = useState({ x: 0, y: 0 });
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const timeoutRef = useRef(null);

    // Calculate best position based on viewport
    const calculatePosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const padding = 8;

        // Check available space in each direction
        const spaceTop = triggerRect.top;
        const spaceBottom = window.innerHeight - triggerRect.bottom;
        const spaceRight = window.innerWidth - triggerRect.right;

        let bestPosition = position;

        if (position === 'auto') {
            // Prefer top, then bottom, then right, then left
            if (spaceTop >= tooltipRect.height + padding) {
                bestPosition = 'top';
            } else if (spaceBottom >= tooltipRect.height + padding) {
                bestPosition = 'bottom';
            } else if (spaceRight >= tooltipRect.width + padding) {
                bestPosition = 'right';
            } else {
                bestPosition = 'left';
            }
        }

        // Calculate coordinates
        let x = 0, y = 0;

        switch (bestPosition) {
            case 'top':
                x = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
                y = triggerRect.top - tooltipRect.height - padding;
                break;
            case 'bottom':
                x = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
                y = triggerRect.bottom + padding;
                break;
            case 'left':
                x = triggerRect.left - tooltipRect.width - padding;
                y = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
                break;
            case 'right':
                x = triggerRect.right + padding;
                y = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
                break;
            default:
                break;
        }

        // Keep within viewport bounds
        x = Math.max(padding, Math.min(x, window.innerWidth - tooltipRect.width - padding));
        y = Math.max(padding, Math.min(y, window.innerHeight - tooltipRect.height - padding));

        setCalculatedPosition(bestPosition);
        setCoords({ x, y });
    }, [position]);

    // Handle mouse enter
    const handleMouseEnter = useCallback(() => {
        if (disabled) return;

        timeoutRef.current = setTimeout(() => {
            setIsVisible(true);
        }, delay);
    }, [delay, disabled]);

    // Handle mouse leave
    const handleMouseLeave = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsVisible(false);
    }, []);

    // Update position when visible
    useEffect(() => {
        if (isVisible) {
            calculatePosition();
        }
    }, [isVisible, calculatePosition]);

    // Update position on scroll/resize
    useEffect(() => {
        if (!isVisible) return;

        const handlePositionUpdate = () => calculatePosition();

        window.addEventListener('scroll', handlePositionUpdate, true);
        window.addEventListener('resize', handlePositionUpdate);

        return () => {
            window.removeEventListener('scroll', handlePositionUpdate, true);
            window.removeEventListener('resize', handlePositionUpdate);
        };
    }, [isVisible, calculatePosition]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // Don't render if no content
    if (!content && !attackText && !defenseText) {
        return children;
    }

    // Determine if we have rich content (attack/defense sections)
    const hasRichContent = attackText || defenseText;

    return (
        <>
            <span
                ref={triggerRef}
                className="tooltip-trigger"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleMouseEnter}
                onBlur={handleMouseLeave}
            >
                {children}
            </span>

            {isVisible && (
                <div
                    ref={tooltipRef}
                    className={`tooltip tooltip-${calculatedPosition} ${hasRichContent ? 'tooltip-rich' : ''}`}
                    style={{
                        left: `${coords.x}px`,
                        top: `${coords.y}px`
                    }}
                    role="tooltip"
                    aria-hidden={!isVisible}
                >
                    {/* Simple tooltip content */}
                    {!hasRichContent && (
                        <div className="tooltip-content">
                            {category && (
                                <span className={`tooltip-category tooltip-category-${category}`}>
                                    {CATEGORY_ICONS[category]} {CATEGORY_LABELS[category]}
                                </span>
                            )}
                            <p className="tooltip-text">{content}</p>
                        </div>
                    )}

                    {/* Rich tooltip with attack/defense sections */}
                    {hasRichContent && (
                        <div className="tooltip-content tooltip-content-rich">
                            {content && (
                                <p className="tooltip-main-text">{content}</p>
                            )}

                            {attackText && (
                                <div className="tooltip-section tooltip-section-attack">
                                    <span className="tooltip-section-label">
                                        {CATEGORY_ICONS.attack} ATTACK:
                                    </span>
                                    <span className="tooltip-section-text">{attackText}</span>
                                </div>
                            )}

                            {defenseText && (
                                <div className="tooltip-section tooltip-section-defense">
                                    <span className="tooltip-section-label">
                                        {CATEGORY_ICONS.defense} DEFEND:
                                    </span>
                                    <span className="tooltip-section-text">{defenseText}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Arrow */}
                    <div className={`tooltip-arrow tooltip-arrow-${calculatedPosition}`} />
                </div>
            )}
        </>
    );
}

/**
 * Security Tooltip - Pre-configured for common security elements
 * Provides consistent attack/defense context
 */
export function SecurityTooltip({
    children,
    element, // 'port', 'cve', 'subdomain', 'technology', 'finding'
    value,
    severity,
    attackText,
    defenseText,
    ...props
}) {
    // Generate default text based on element type
    const getDefaultContent = () => {
        switch (element) {
            case 'port':
                return `Port ${value} is exposed to the network.`;
            case 'cve':
                return `${value} - Known vulnerability detected.`;
            case 'subdomain':
                return `Subdomain discovered: ${value}`;
            case 'technology':
                return `Technology detected: ${value}`;
            case 'finding':
                return `Security finding with ${severity || 'unknown'} severity.`;
            default:
                return '';
        }
    };

    return (
        <Tooltip
            content={getDefaultContent()}
            attackText={attackText}
            defenseText={defenseText}
            {...props}
        >
            {children}
        </Tooltip>
    );
}

export default Tooltip;
